import './FilteredEmptyState.scss';
import { FilterColumnType, Option } from '@blisspointmedia/bpm-types/dist/FilterBar';

interface FilteredEmptyStateProps {
  tokens: Option[];
}

// Renders a FilterBar token sequence as a human-readable query string. The FilterBar produces
// a flat token stream mixing structural markers (parens), column references (CAT), operators
// (OP), and values (VAL / STRING / BOOL / BOOLEAN). Rendering each type differently gives
// output that reads like SQL rather than a debug dump.
//
//   Tokens: [(  Name  is like  asdfqwer  )]
//   Types:  [OPEN_PAREN, CAT, OP, VAL, CLOSE_PAREN]
//   Output: (Name IS LIKE "asdfqwer")
//
// Basic-mode queries only ever produce a single VAL token, in which case the output is just
// `"asdfqwer"` -- no parens, no column, no operator. Advanced-mode queries can nest.
const renderTokens = (tokens: Option[]): string => {
  const parts = tokens.map(token => {
    const text = token.label ?? token.value ?? '';
    switch (token.type) {
      case FilterColumnType.OPEN_PAREN:
        return '(';
      case FilterColumnType.CLOSE_PAREN:
        return ')';
      case FilterColumnType.OP:
        return text.toUpperCase();
      case FilterColumnType.VAL:
      case FilterColumnType.STRING:
      case FilterColumnType.BOOL:
      case FilterColumnType.BOOLEAN:
        return `"${text}"`;
      default:
        return text;
    }
  });
  // Space-join, then tighten whitespace adjacent to parens so the output reads as
  // `(Name IS LIKE "x")` rather than `( Name IS LIKE "x" )`.
  return parts.join(' ').replace(/\(\s+/g, '(').replace(/\s+\)/g, ')').trim();
};

// Rendered by BPMTable when the filter bar has active tokens but no rows match. Consumers
// don't need to opt in -- BPMTable pre-empts its caller's noRowsRenderer for this specific
// state so the "no matches" copy is consistent across every table in the app. The truly-empty
// state (no underlying data at all) still routes to the caller's noRowsRenderer.
export const FilteredEmptyState = ({ tokens }: FilteredEmptyStateProps) => {
  const query = renderTokens(tokens);
  return (
    <div className='bpm-table-filtered-empty'>
      {query ? `No results for ${query}.` : 'No results match the current filter.'}
    </div>
  );
};
